import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, Send, Loader2, CalendarDays, Building2, Mic2 } from "lucide-react";
import ShareStrip from "@/components/common/ShareStrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GuestRequestModal = ({ open, onClose }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    business_website: "",
    topic: "",
    preferred_date: "",
    preferred_time: "",
    social_handle: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const reset = () =>
    setForm({
      name: "",
      email: "",
      phone: "",
      business_name: "",
      business_website: "",
      topic: "",
      preferred_date: "",
      preferred_time: "",
      social_handle: "",
      message: "",
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.business_name || !form.topic) {
      toast.error("Please fill in your name, email, business, and topic.");
      return;
    }
    // Dismiss any prior toasts so the success message lands cleanly
    toast.dismiss();
    setLoading(true);
    try {
      await axios.post(`${API}/guest-requests`, form);
      toast.success(
        "Request sent — you'll hear back within 48 hours once it's reviewed.",
      );
      reset();
      onClose();
    } catch (err) {
      toast.error("Couldn't send request. Try again or email us.");
    } finally {
      setLoading(false);
    }
  };

  const input =
    "w-full bg-white/[0.03] border border-white/10 focus:border-[#00f0ff]/60 focus:bg-white/[0.05] focus:outline-none rounded-xl px-4 py-3 text-[15px] text-white placeholder:text-white/30 transition-all";
  const label =
    "block text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mb-2";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain flex items-start md:items-center justify-center p-4 md:p-8"
          data-testid="guest-request-modal"
        >
          <ShareStrip campaign="spotlight_guest_request" title="Spotlight guest slot — SB Media Studio" text="Free podcast guest spot for Central Florida founders. Grab yours." href="/#spotlight" className="mr-1" />
          <button
            aria-label="Close"
            onClick={onClose}
            data-testid="guest-modal-overlay"
            className="fixed inset-0 bg-black/80 backdrop-blur-md cursor-default"
          />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-2xl my-4 md:my-0 rounded-3xl bg-[#0a0612] border border-white/10 glow-border"
          >
            <div className="flex items-start justify-between gap-4 px-7 pt-7 pb-5 bg-[#0a0612] rounded-t-3xl">
              <div>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#ff0055]">
                  <Mic2 size={12} /> Small Business Spotlight
                </div>
                <h3 className="font-display font-black text-2xl md:text-3xl mt-2 tracking-tight">
                  Request your guest spot
                </h3>
                <p className="text-[#a395c3] text-sm mt-2 max-w-md">
                  Submit your story below — Sebastian reviews every request
                  personally. Approved guests receive a confirmation with the
                  final session details.
                </p>
              </div>
              <button
                onClick={onClose}
                data-testid="guest-modal-close"
                aria-label="Close modal"
                className="flex-shrink-0 w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center"
              >
                <X size={16} />
              </button>
            </div>

            <form
              onSubmit={submit}
              className="px-7 pb-7 space-y-4"
              data-testid="guest-request-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={label}>Your name *</label>
                  <input
                    type="text"
                    data-testid="guest-input-name"
                    value={form.name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, name: e.target.value }))
                    }
                    placeholder="Alex Rivera"
                    className={input}
                  />
                </div>
                <div>
                  <label className={label}>Email *</label>
                  <input
                    type="email"
                    data-testid="guest-input-email"
                    value={form.email}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, email: e.target.value }))
                    }
                    placeholder="alex@brand.com"
                    className={input}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={label}>Phone</label>
                  <input
                    type="tel"
                    data-testid="guest-input-phone"
                    value={form.phone}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, phone: e.target.value }))
                    }
                    placeholder="+1 (555) 010-0123"
                    className={input}
                  />
                </div>
                <div>
                  <label className={label}>Instagram handle</label>
                  <input
                    type="text"
                    data-testid="guest-input-social"
                    value={form.social_handle}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, social_handle: e.target.value }))
                    }
                    placeholder="@yourbrand"
                    className={input}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={label}>
                    <Building2 size={11} className="inline -mt-0.5 mr-1" />
                    Business name *
                  </label>
                  <input
                    type="text"
                    data-testid="guest-input-business"
                    value={form.business_name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, business_name: e.target.value }))
                    }
                    placeholder="Rivera Coffee Co."
                    className={input}
                  />
                </div>
                <div>
                  <label className={label}>Website</label>
                  <input
                    type="url"
                    data-testid="guest-input-website"
                    value={form.business_website}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        business_website: e.target.value,
                      }))
                    }
                    placeholder="https://"
                    className={input}
                  />
                </div>
              </div>

              <div>
                <label className={label}>
                  Topic / what you&apos;d love to talk about *
                </label>
                <input
                  type="text"
                  data-testid="guest-input-topic"
                  value={form.topic}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, topic: e.target.value }))
                  }
                  placeholder="Building a small business from a side-hustle"
                  className={input}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={label}>
                    <CalendarDays size={11} className="inline -mt-0.5 mr-1" />
                    Preferred date
                  </label>
                  <input
                    type="date"
                    data-testid="guest-input-date"
                    value={form.preferred_date}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, preferred_date: e.target.value }))
                    }
                    className={`${input} [color-scheme:dark]`}
                  />
                </div>
                <div>
                  <label className={label}>Preferred time</label>
                  <input
                    type="time"
                    data-testid="guest-input-time"
                    value={form.preferred_time}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, preferred_time: e.target.value }))
                    }
                    className={`${input} [color-scheme:dark]`}
                  />
                </div>
              </div>

              <div>
                <label className={label}>Anything else we should know?</label>
                <textarea
                  data-testid="guest-input-message"
                  value={form.message}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, message: e.target.value }))
                  }
                  placeholder="Story angle, scheduling constraints, link to your work..."
                  rows={4}
                  className={`${input} resize-none`}
                />
              </div>

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-3">
                <div className="text-[11px] text-[#a395c3]">
                  By submitting, you confirm you&apos;d like to be featured. We&apos;ll
                  reply within 48 hours.
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="guest-submit-button"
                  className="group inline-flex items-center justify-center gap-3 pl-6 pr-3 py-3 rounded-full bg-[#ff0055] text-white font-semibold hover:bg-[#00f0ff] hover:text-[#05050a] transition-colors disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      Sending
                      <Loader2 size={16} className="animate-spin" />
                    </>
                  ) : (
                    <>
                      Submit request
                      <span className="w-9 h-9 rounded-full bg-black/30 text-white flex items-center justify-center group-hover:rotate-[-45deg] transition-transform">
                        <Send size={14} />
                      </span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GuestRequestModal;
